import { useState } from 'owa-react';
import { observer } from 'owa-mobx-react';
import { useComputed, useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { newFavoritePickedText, renameFavoriteLabel } from './FavoritesList.locstring.json';
import loc, { format } from 'owa-localize';
import { getStore as getSharedFavoritesStore } from 'owa-favorites';
import { FavoritesPicker } from 'owa-mail-favorites-picker';
import { getMailFavoritesViewState } from 'owa-mail-favorites-store';
import { getEffectiveFolderDisplayName, getFolderTable } from 'owa-folders';
import { FAVORITE_FOLDERS_TREE_TYPE } from 'owa-folders-constants';
import React from 'react';
import FavoriteNode from './FavoriteNode';
import type { FavoriteFolderData } from 'owa-favorites-types';
import { getStore } from 'owa-mail-folder-store/lib/store/store';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import { getMailboxInfoFromFolderId } from 'owa-mail-mailboxinfo';
import { FolderOperationNode } from 'owa-mail-folder-view';
import type { MailboxInfo } from 'owa-client-types';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list';
import { full, medium, compact, nodeHeight } from 'owa-tree-node/lib/components/NodeHeight.scss';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { StarRegular } from '@fluentui/react-icons';
import { starIcon, starIconFull, starIconMedium, addFavoriteContainer } from './FavoritesList.scss';

import classnames from 'owa-classnames';
import { hideFolderPaneAfterSideRailInteraction } from '../utils/folderPaneSideRailInteraction';

export interface FavoritesListProps {
    mailboxInfo: MailboxInfo;
}

const defaultExport: React.FunctionComponent<FavoritesListProps> = observer(function FavoritesList(
    props: FavoritesListProps
) {
    const favoritesStore = useComputed(() => {
        return getSharedFavoritesStore();
    });

    const accountKey = getAccountKeyForMailboxInfo(props.mailboxInfo);

    const orderedOutlookFavoritesIds = isMonarchMultipleAccountsEnabled()
        ? favoritesStore.get().orderedCombinedOutlookFavoritesIds
        : favoritesStore.get().favoriteTreeData.get(accountKey)?.orderedOutlookFavoritesIds;

    if (!orderedOutlookFavoritesIds) {
        return null;
    }

    return (
        <>
            {orderedOutlookFavoritesIds?.map(favoriteId => (
                <FavoriteNodeWrapper key={favoriteId} favoriteId={favoriteId} />
            ))}
            <AddFavoriteWrapper mailboxInfo={props.mailboxInfo} />
        </>
    );
}, 'FavoritesList');
export default defaultExport;

const shouldRenderNodeInEditMode = (favoriteId: string): boolean => {
    const folderTextFieldViewState = getStore().folderTextFieldViewState;
    return (
        !!folderTextFieldViewState &&
        folderTextFieldViewState.folderId == favoriteId &&
        folderTextFieldViewState.folderTreeType === FAVORITE_FOLDERS_TREE_TYPE
    );
};

export interface FavoriteNodeWrapperProps {
    favoriteId: string;
}
const FavoriteNodeWrapper = observer(function FavoriteNodeWrapper(props: FavoriteNodeWrapperProps) {
    const { favoriteId } = props;
    const favoritesStore = useComputed(() => {
        return getSharedFavoritesStore();
    });
    const renderInEditMode = useComputedValue((): boolean => {
        return shouldRenderNodeInEditMode(favoriteId);
    }, [favoriteId]);
    const autoCollapseFolderPaneOnDismiss =
        getStore().folderTextFieldViewState?.autoCollapseFolderPaneOnDismiss;

    const onOperationComplete = React.useCallback(() => {
        hideFolderPaneAfterSideRailInteraction();
    }, []);

    if (renderInEditMode) {
        const folderId = (
            favoritesStore.get().outlookFavorites.get(favoriteId) as FavoriteFolderData
        ).folderId;
        const currentFolderName = getEffectiveFolderDisplayName(getFolderTable().get(folderId));
        return (
            <FolderOperationNode
                key="favoriteTextField"
                folderId={folderId}
                treeType={FAVORITE_FOLDERS_TREE_TYPE}
                nestDepth={0}
                operationType={'rename'}
                originalValue={currentFolderName}
                mailboxInfo={getMailboxInfoFromFolderId(folderId)}
                ariaAnnouncementLabel={renameFavoriteLabel}
                onOperationComplete={
                    autoCollapseFolderPaneOnDismiss ? onOperationComplete : undefined
                }
            />
        );
    }
    return <FavoriteNode key={favoriteId} favoriteId={favoriteId} ellipsesOnHover={true} />;
}, 'FavoriteNodeWrapper');

export interface AddFavoriteWrapperProps {
    mailboxInfo: MailboxInfo;
}
const AddFavoriteWrapper = observer(function AddFavoriteWrapper(props: AddFavoriteWrapperProps) {
    const accountKey = getAccountKeyForMailboxInfo(props.mailboxInfo);
    const [favoriteUpdateText, setFavoriteUpdateText] = useState<string>('');

    const onItemSelected = React.useCallback((displayName: string) => {
        setFavoriteUpdateText(format(loc(newFavoritePickedText), displayName));
    }, []);

    const shouldShowFavoritesPicker =
        getMailFavoritesViewState(accountKey)?.shouldShowFindFavoritesPicker;
    if (isGlobalFeatureEnabled('fp-add-favorite-improvements')) {
        return (
            <>
                {shouldShowFavoritesPicker && (
                    <div
                        className={classnames(
                            getDensityModeCssClass(full, medium, compact),
                            nodeHeight,
                            addFavoriteContainer
                        )}
                    >
                        <span
                            className={classnames(
                                starIcon,
                                getDensityModeCssClass(starIconFull, starIconMedium, undefined)
                            )}
                        >
                            <StarRegular />
                        </span>
                        <FavoritesPicker
                            mailboxInfo={props.mailboxInfo}
                            onItemSelected={onItemSelected}
                        />
                    </div>
                )}
                <span className="screenReaderOnly" aria-live="assertive" aria-atomic="true">
                    {favoriteUpdateText}
                </span>
            </>
        );
    } else {
        return (
            <>
                {shouldShowFavoritesPicker && (
                    <div
                        className={classnames(
                            getDensityModeCssClass(full, medium, compact),
                            nodeHeight
                        )}
                    >
                        <FavoritesPicker
                            mailboxInfo={props.mailboxInfo}
                            onItemSelected={onItemSelected}
                        />
                    </div>
                )}
                <span className="screenReaderOnly" aria-live="assertive" aria-atomic="true">
                    {favoriteUpdateText}
                </span>
            </>
        );
    }
}, 'AddFavoriteWrapper');
